<script setup lang="ts">
import { computed } from 'vue'
import type { GalleryImage, ImageVariant } from '@/types/galleries'
import { fallbackUrl, pickVariant, variantSrcset } from '@/utils/galleries/imageSources'

const props = withDefaults(
  defineProps<{
    image: GalleryImage
    alt?: string
    /** Variant used for the fallback <img> src. */
    variant?: ImageVariant['name']
    sizes?: string
    imgClass?: string
  }>(),
  {
    alt: '',
    variant: 'thumb',
    sizes: '(min-width: 1024px) 200px, 33vw',
    imgClass: 'w-full h-full object-cover',
  },
)

const avif = computed(() => variantSrcset(props.image.variants, 'avif'))
const webp = computed(() => variantSrcset(props.image.variants, 'webp'))
const jpeg = computed(() => variantSrcset(props.image.variants, 'jpeg'))
const fallback = computed(() => {
  const variant = pickVariant(props.image, props.variant)
  return variant === null ? null : fallbackUrl(variant)
})
</script>

<template>
  <picture v-if="fallback !== null">
    <source v-if="avif" type="image/avif" :srcset="avif" :sizes="sizes" />
    <source v-if="webp" type="image/webp" :srcset="webp" :sizes="sizes" />
    <source v-if="jpeg" type="image/jpeg" :srcset="jpeg" :sizes="sizes" />
    <img
      :src="fallback"
      :alt="alt"
      :class="imgClass"
      :width="image.width"
      :height="image.height"
      loading="lazy"
      decoding="async"
      draggable="false"
    />
  </picture>
</template>
